import { makeAutoObservable, runInAction } from 'mobx';
import type { GetProductsUseCase } from '@/core/application/use-cases/product/GetProductsUseCase';
import type { GetPaymentPlansUseCase } from '@/core/application/use-cases/payment/GetPaymentPlansUseCase';
import type { ApiProductRepository } from '@/core/infrastructure/repositories/ApiProductRepository';
import type { PSSPlansResponse } from '@/core/infrastructure/services/ApiPaymentService';
import {
  ApiCategoriesResponse,
  ApiFiltersResponse,
  ApiHomepageCollections,
  PaymentOption,
  Product,
  ProductFilters,
} from '@/core/domain/entities/Product';
import {
  ProductFiltersRequest,
  ProductSearchRequest,
} from '@/core/application/dtos/ProductDto';

export class ProductStore {
  // State
  products: Product[] = [];
  featuredProducts: Product[] = [];
  currentProduct: Product | null = null;
  searchResults: Product[] = [];
  recommendedProducts: Product[] = [];
  relatedProducts: Product[] = [];
  productCollections: any = null;

  // New state for missing features
  productFilters: ApiFiltersResponse | null = null;
  categoryNavigation: ApiCategoriesResponse['results'] = [];
  categoryHierarchy: ApiCategoriesResponse['results'] = [];
  homepageCollections: ApiHomepageCollections['sections'] = [];

  // Payment-related state
  paymentOptions: PaymentOption[] = [];
  pssPlans: PSSPlansResponse | null = null;
  isLoadingPaymentOptions = false;
  isLoadingPSSPlans = false;

  isLoading = false;
  isSearching = false;
  error: string | null = null;
  searchQuery = '';
  currentFilters: ProductFiltersRequest = {};

  // Category page specific state
  categoryProducts: Product[] = [];
  categoryHasMoreProducts = true;
  categoryCurrentOffset = 0;
  categorySelectedPriceRange = '--';
  categorySelectedBrand = '--';
  categorySelectedSort = '--';
  categorySelectedTags: string[] = [];
  isLoadingCategory = false;

  constructor(
    private readonly getProductsUseCase: GetProductsUseCase,
    private readonly getPaymentPlansUseCase: GetPaymentPlansUseCase,
    private readonly productRepository: ApiProductRepository
  ) {
    makeAutoObservable(this);
  }

  // Computed values
  get hasSearchResults(): boolean {
    return this.searchResults.length > 0;
  }

  get hasProducts(): boolean {
    return this.products.length > 0;
  }

  get hasFeaturedProducts(): boolean {
    return this.featuredProducts.length > 0;
  }

  get hasRecommendedProducts(): boolean {
    return this.recommendedProducts.length > 0;
  }

  get hasRelatedProducts(): boolean {
    return this.relatedProducts.length > 0;
  }

  get isProductLoaded(): boolean {
    return this.currentProduct !== null;
  }

  get availableProducts(): Product[] {
    return this.products.filter((product) => product.isAvailable);
  }

  get featuredAvailableProducts(): Product[] {
    return this.featuredProducts.filter((product) => product.isAvailable);
  }

  // Payment-related computed values
  get hasPaymentOptions(): boolean {
    return this.paymentOptions !== null;
  }

  get hasPSSPlans(): boolean {
    return this.pssPlans !== null;
  }

  get isPSSAvailable(): boolean {
    return (
      this.paymentOptions?.some(
        (option: any) => option.type === 'paysmallsmall' && option.available
      ) || false
    );
  }

  get isPSSCreditAvailable(): boolean {
    return (
      this.paymentOptions?.some(
        (option: any) => option.type === 'pss_credit' && option.available
      ) || false
    );
  }

  get isUserEligibleForPSS(): boolean {
    return this.pssPlans?.userEligibility?.pssCreditEligible || false;
  }

  get recommendedPSSPlan(): any {
    if (!this.pssPlans) return null;

    const recommendedDuration =
      this.pssPlans.userEligibility.recommendedDuration;
    return this.pssPlans.durationOptions.find(
      (option: any) => option.months === recommendedDuration
    );
  }

  get availablePaymentMethods(): any[] {
    return this.paymentOptions?.filter((option: any) => option.available) || [];
  }

  // New computed values for missing features
  get hasProductFilters(): boolean {
    return this.productFilters !== null;
  }

  get hasCategoryNavigation(): boolean {
    return this.categoryNavigation.length > 0;
  }

  get hasCategoryHierarchy(): boolean {
    return this.categoryHierarchy !== null;
  }

  get hasHomepageCollections(): boolean {
    return this.homepageCollections !== null;
  }

  get availableCategories(): any[] {
    return (
      this.categoryNavigation.filter(
        (category: any) => category.is_featured_menu
      ) || []
    );
  }

  get productPriceRange(): { min: number; max: number } {
    if (
      !this.productFilters?.price_range ||
      this.productFilters.price_range.length === 0
    ) {
      return { min: 0, max: 10000 };
    }

    // Find the overall min and max from all price ranges
    const min = Math.min(...this.productFilters.price_range.map((p) => p.min));
    const max = Math.max(...this.productFilters.price_range.map((p) => p.max));

    return { min, max };
  }

  get availableBrands(): string[] {
    return this.productFilters?.brands?.map((v) => v.name) || [];
  }

  get hasCategoryFiltersApplied(): boolean {
    return (
      this.categorySelectedPriceRange !== '--' ||
      this.categorySelectedBrand !== '--' ||
      this.categorySelectedSort !== '--' ||
      this.categorySelectedTags.length > 0
    );
  }

  // Actions
  async loadProducts(filters?: ProductFiltersRequest): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getAll(filters);

      runInAction(() => {
        this.products = products;
        this.currentFilters = filters || {};
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to load products'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadFeaturedProducts(limit: number = 10): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getFeatured(limit);

      runInAction(() => {
        this.featuredProducts = products;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load featured products'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductById(id: string): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const product = await this.getProductsUseCase.getById(id);

      runInAction(() => {
        this.currentProduct = product;
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to load product'
      );
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductsByCategory(
    categoryId: string,
    filters?: ProductFiltersRequest
  ): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getByCategory(
        categoryId,
        filters
      );

      runInAction(() => {
        this.products = products.results;
        this.currentFilters = { ...filters, categories: [categoryId] };
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load products by category'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadProductsByShop(
    shopId: string,
    filters?: ProductFiltersRequest
  ): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.getByShop(shopId, filters);

      runInAction(() => {
        this.products = products;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load products by shop'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async searchProducts(request: ProductSearchRequest): Promise<void> {
    this.setSearching(true);
    this.setError(null);

    try {
      const products = await this.getProductsUseCase.search(
        request.query,
        request.filters
      );

      runInAction(() => {
        this.searchResults = products;
        this.searchQuery = request.query;
        this.currentFilters = request.filters || {};
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to search products'
      );
    } finally {
      this.setSearching(false);
    }
  }

  async loadRecommendedProducts(
    userId: string,
    limit: number = 10
  ): Promise<void> {
    try {
      const products = await this.getProductsUseCase.getRecommendations(
        userId,
        limit
      );

      runInAction(() => {
        this.recommendedProducts = products;
      });
    } catch (_error) {
      // Best-effort: recommended products should not block the page.
    }
  }

  async loadRelatedProducts(
    productId: string,
    limit: number = 5
  ): Promise<void> {
    try {
      const products = await this.getProductsUseCase.getRelated(
        productId,
        limit
      );

      runInAction(() => {
        this.relatedProducts = products;
      });
    } catch (_error) {
      // Best-effort: related products should not block the page.
    }
  }

  // async loadProductCollections(): Promise<void> {
  //   this.setLoading(true);
  //   this.setError(null);
  //
  //   try {
  //     const collections = await this.productRepository.findCollections();
  //
  //     runInAction(() => {
  //       this.productCollections = collections;
  //     });
  //   } catch (error) {
  //     this.setError(
  //       error instanceof Error
  //         ? error.message
  //         : 'Failed to load product collections'
  //     );
  //   } finally {
  //     this.setLoading(false);
  //   }
  // }

  async loadProductFilters(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const filters = await this.productRepository.getProductFilters?.();
      runInAction(() => {
        this.productFilters = filters ?? null;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load product filters'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadCategoryNavigation(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const navigation = await this.productRepository.getCategoryNavigation();

      runInAction(() => {
        this.categoryNavigation = navigation.results || navigation;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load category navigation'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async getFeaturedCategories(isFeatured: boolean = true): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const hierarchy = await this.productRepository.getFeaturedCategories(
        isFeatured
      );

      runInAction(() => {
        this.categoryHierarchy = hierarchy.results;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load category hierarchy'
      );
    } finally {
      this.setLoading(false);
    }
  }

  async loadHomepageCollections(): Promise<void> {
    this.setLoading(true);
    this.setError(null);

    try {
      const collections = await this.productRepository.getHomepageCollections();

      runInAction(() => {
        // Backend should handle homepage diversity, but keep a client-side fallback so
        // the UI never shows the same product (or near-duplicate SKU) repeatedly.
        this.homepageCollections = this.diversifyHomepageCollections(
          collections.sections
        );
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load homepage collections'
      );
    } finally {
      this.setLoading(false);
    }
  }

  private diversifyHomepageCollections(
    sections: ApiHomepageCollections['sections']
  ): ApiHomepageCollections['sections'] {
    const globalSeenIds = new Set<string>();
    const globalSeenGroupKeys = new Set<string>();
    let recentSeenIds = new Set<string>();
    let recentSeenGroupKeys = new Set<string>();

    return sections
      .map((section) => {
        const products = section.results ?? [];
        const limit = products.length;
        if (limit === 0) {
          return section;
        }

        const fresh: typeof products = [];
        const seenNotRecent: typeof products = [];
        const seenRecent: typeof products = [];

        for (const product of products) {
          const productId = String(product.id);
          const groupKey = this.getHomepageProductGroupKey(product);

          const isRecent =
            recentSeenIds.has(productId) || recentSeenGroupKeys.has(groupKey);
          const isSeen =
            globalSeenIds.has(productId) || globalSeenGroupKeys.has(groupKey);

          if (!isSeen && !isRecent) {
            fresh.push(product);
          } else if (isSeen && !isRecent) {
            seenNotRecent.push(product);
          } else {
            seenRecent.push(product);
          }
        }

        const selected: typeof products = [];
        const selectedIds = new Set<string>();
        const selectedGroupKeys = new Set<string>();

        const consider = (pool: typeof products, allowGroupRepeats: boolean) => {
          for (const product of pool) {
            if (selected.length >= limit) return;

            const productId = String(product.id);
            const groupKey = this.getHomepageProductGroupKey(product);

            if (!allowGroupRepeats && selectedGroupKeys.has(groupKey)) {
              continue;
            }

            selected.push(product);
            selectedIds.add(productId);
            selectedGroupKeys.add(groupKey);
          }
        };

        // Prefer new-to-page items, then allow repeats (but push last-row repeats to the end).
        consider(fresh, false);
        consider(seenNotRecent, false);
        consider(seenRecent, false);

        // Last resort: allow group repeats to keep rows full.
        if (selected.length < limit) {
          consider(
            products.filter((product) => !selectedIds.has(String(product.id))),
            true
          );
        }

        recentSeenIds = new Set(selected.map((product) => String(product.id)));
        recentSeenGroupKeys = new Set(
          selected.map((product) => this.getHomepageProductGroupKey(product))
        );
        for (const product of selected) {
          const productId = String(product.id);
          const groupKey = this.getHomepageProductGroupKey(product);
          globalSeenIds.add(productId);
          globalSeenGroupKeys.add(groupKey);
        }

        return {
          ...section,
          results: selected,
        };
      })
      .filter((section) => (section.results ?? []).length > 0);
  }

  private normalizeHomepageDedupeText(value: unknown): string {
    if (typeof value !== 'string') return '';
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
  }

  private getHomepageProductGroupKey(product: any): string {
    const name = this.normalizeHomepageDedupeText(product?.name);
    const brand = this.normalizeHomepageDedupeText(
      typeof product?.brand === 'string' ? product.brand : product?.brand?.name
    );
    const category = this.normalizeHomepageDedupeText(
      typeof product?.category === 'string'
        ? product.category
        : product?.category?.slug
    );
    const groupKey = [category, brand, name].filter(Boolean).join('|');
    return groupKey || String(product?.id);
  }

  async loadPaymentOptions(productId: string): Promise<void> {
    this.isLoadingPaymentOptions = true;
    this.setError(null);

    try {
      const options = await this.productRepository.getPaymentOptions(productId);

      runInAction(() => {
        this.paymentOptions = options.payment_options;
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load payment options'
      );
    } finally {
      this.isLoadingPaymentOptions = false;
    }
  }

  async loadPSSPlans(productId: string, userId: string): Promise<void> {
    this.isLoadingPSSPlans = true;
    this.setError(null);

    try {
      const plans = await this.getPaymentPlansUseCase.execute({
        productId,
        userId,
      });

      runInAction(() => {
        this.pssPlans = plans;
      });
    } catch (error) {
      this.setError(
        error instanceof Error ? error.message : 'Failed to load PSS plans'
      );
      throw error;
    } finally {
      this.isLoadingPSSPlans = false;
    }
  }

  // Helper methods
  setCurrentProduct(product: Product | null): void {
    this.currentProduct = product;
    // Clear payment-related data when changing products
    this.paymentOptions = [];
    this.pssPlans = null;
  }

  clearPaymentData(): void {
    this.paymentOptions = [];
    this.pssPlans = null;
  }

  calculateMonthlyPayment(
    productPrice: number,
    durationMonths: number,
    kmpEnabled: boolean = false
  ): number {
    let baseAmount = productPrice / durationMonths;

    if (kmpEnabled && this.pssPlans?.kmpPolicy) {
      const kmpFee =
        (productPrice * this.pssPlans.kmpPolicy.basePercentage) / 100;
      baseAmount += kmpFee / durationMonths;
    }

    return baseAmount;
  }

  getPSSPlanByDuration(months: number): any {
    return this.pssPlans?.durationOptions?.find(
      (option) => option.months === months
    );
  }

  getProductCollectionByKey(key: string): any {
    return this.productCollections?.sections?.find(
      (section: any) => section.key === key
    );
  }

  clearSearchResults(): void {
    this.searchResults = [];
    this.searchQuery = '';
  }

  clearFilters(): void {
    this.currentFilters = {};
  }

  getProductsByPriceRange(min: number, max: number): Product[] {
    return this.products.filter(
      (product) => product.retail_price >= min && product.retail_price <= max
    );
  }

  getProductsByCategory(category: string): Product[] {
    return this.products.filter((product) =>
      product.categories.includes(category)
    );
  }

  // Category page specific methods
  setCategoryFilter(
    filterType: 'priceRange' | 'brand' | 'sort' | 'tags',
    value: string | string[]
  ): void {
    runInAction(() => {
      switch (filterType) {
        case 'priceRange':
          this.categorySelectedPriceRange = value as string;
          break;
        case 'brand':
          this.categorySelectedBrand = value as string;
          break;
        case 'sort':
          this.categorySelectedSort = value as string;
          break;
        case 'tags':
          this.categorySelectedTags = value as string[];
          break;
      }
    });
  }

  clearCategoryFilters(): void {
    runInAction(() => {
      this.categorySelectedPriceRange = '--';
      this.categorySelectedBrand = '--';
      this.categorySelectedSort = '--';
      this.categorySelectedTags = [];
    });
  }

  async loadCategoryProducts(
    categories: string[],
    reset = false,
    itemsPerPage = 20
  ): Promise<void> {
    if (this.isLoadingCategory) return;

    this.isLoadingCategory = true;
    this.setError(null);

    try {
      const filters = this.buildCategoryFilters(
        categories,
        itemsPerPage,
        reset ? 0 : this.categoryCurrentOffset
      );

      if (reset) {
        this.categoryCurrentOffset = 0;
      }

      const result = await this.getProductsUseCase.getByCategory(
        categories[0] || '',
        filters
      );

      runInAction(() => {
        if (reset) {
          this.categoryProducts = result.results;
        } else {
          // For pagination, append new products
          const existingIds = new Set(this.categoryProducts.map((p) => p.id));
          const newProducts = result.results.filter(
            (p) => !existingIds.has(p.id)
          );
          this.categoryProducts = [...this.categoryProducts, ...newProducts];
        }

        // Check if there are more products to load
        this.categoryHasMoreProducts = result.results.length === itemsPerPage;

        if (!reset) {
          this.categoryCurrentOffset += itemsPerPage;
        }
      });
    } catch (error) {
      this.setError(
        error instanceof Error
          ? error.message
          : 'Failed to load category products'
      );
    } finally {
      this.isLoadingCategory = false;
    }
  }

  async reloadCategoryWithFilters(
    categories: string[],
    itemsPerPage = 20
  ): Promise<void> {
    runInAction(() => {
      this.categoryProducts = [];
      this.categoryCurrentOffset = 0;
      this.categoryHasMoreProducts = true;
    });
    await this.loadCategoryProducts(categories, true, itemsPerPage);
  }

  async loadMoreCategoryProducts(
    categories: string[],
    itemsPerPage = 20
  ): Promise<void> {
    if (this.isLoadingCategory || !this.categoryHasMoreProducts) return;
    await this.loadCategoryProducts(categories, false, itemsPerPage);
  }

  private setLoading(loading: boolean): void {
    runInAction(() => {
      this.isLoading = loading;
    });
  }

  private setSearching(searching: boolean): void {
    this.isSearching = searching;
  }

  private setError(error: string | null): void {
    this.error = error;
  }

  private buildCategoryFilters(
    categories: string[],
    limit: number,
    offset: number
  ): ProductFilters {
    const filters: ProductFilters = {
      categories: categories.map((cat) => cat.toLowerCase()),
      limit,
      offset,
    };

    // Add price range filter
    if (this.categorySelectedPriceRange !== '--') {
      const [min, max] = this.categorySelectedPriceRange.split('-').map(Number);
      filters.minPrice = min;
      filters.maxPrice = max;
    }

    // Add brand filter
    if (this.categorySelectedBrand !== '--') {
      filters.brandName = this.categorySelectedBrand;
    }

    // Add tag filters
    if (this.categorySelectedTags.length > 0) {
      filters.tags = this.categorySelectedTags;
    }

    // Add sorting
    if (this.categorySelectedSort !== '--') {
      if (this.categorySelectedSort === 'lowprice') {
        filters.sortBy = 'price';
        filters.sortOrder = 'asc';
      } else if (this.categorySelectedSort === 'highprice') {
        filters.sortBy = 'price';
        filters.sortOrder = 'desc';
      } else if (this.categorySelectedSort === 'name') {
        filters.sortBy = 'name';
        filters.sortOrder = 'asc';
      }
    }

    return filters;
  }
}

export interface NavigationCategory {
  id: string;
  slug: string;
  name: string;
  icon_url?: string;
  product_count?: number;
  sort_order?: number;
  is_featured: boolean;
  is_featured_menu: boolean; // New field for top nav curation
  category_id?: string; // Legacy support
  category_name?: string; // Legacy support
}

export interface NavigationHierarchy extends NavigationCategory {
  parent_id?: string | null;
  banner_image_url?: string;
  children?: NavigationHierarchy[];
}

export interface HomeCollection {
  key: string;
  title: string;
  description: string;
  products: Product[];
}
